import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  FileUp,
  Link as LinkIcon,
  FileText,
  ChevronDown,
} from "lucide-react";
import {
  FileUploadModal,
  LinkAdditionModal,
  SnippetCreationModal,
} from "./modals";
import { KnowledgeItem } from "@/services/fileService";

interface AddKnowledgeDropdownProps {
  onSuccess?: (item?: KnowledgeItem) => void;
  agentId?: string | null;
  initialLinkValues?: {
    title?: string;
    url?: string;
    description?: string;
  };
}

const AddKnowledgeDropdown = ({
  onSuccess,
  agentId,
  initialLinkValues,
}: AddKnowledgeDropdownProps) => {
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [snippetModalOpen, setSnippetModalOpen] = useState(false);

  const handleSuccess = (item?: KnowledgeItem) => {
    // Close all modals
    setFileModalOpen(false);
    setLinkModalOpen(false);
    setSnippetModalOpen(false);

    // Call parent success handler
    if (onSuccess) {
      onSuccess(item);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add Knowledge
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={() => setLinkModalOpen(true)}>
            <LinkIcon className="mr-2 h-4 w-4 text-green-600" />
            <div className="flex flex-col">
              <span className="font-medium">From a Link</span>
              <span className="text-xs text-muted-foreground">
                Add website or webpage
              </span>
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => setFileModalOpen(true)}>
            <FileUp className="mr-2 h-4 w-4 text-blue-600" />
            <div className="flex flex-col">
              <span className="font-medium">From a File</span>
              <span className="text-xs text-muted-foreground">
                Upload .pdf, .doc, or .txt
              </span>
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => setSnippetModalOpen(true)}>
            <FileText className="mr-2 h-4 w-4 text-purple-600" />
            <div className="flex flex-col">
              <span className="font-medium">Text Snippet</span>
              <span className="text-xs text-muted-foreground">
                Add custom text content
              </span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Modals */}
      <FileUploadModal
        open={fileModalOpen}
        onOpenChange={setFileModalOpen}
        onSuccess={handleSuccess}
        agentId={agentId}
      />

      <LinkAdditionModal
        open={linkModalOpen}
        onOpenChange={setLinkModalOpen}
        onSuccess={handleSuccess}
        agentId={agentId}
        initialValues={initialLinkValues}
      />

      <SnippetCreationModal
        open={snippetModalOpen}
        onOpenChange={setSnippetModalOpen}
        onSuccess={handleSuccess}
        agentId={agentId}
      />
    </>
  );
};

export default AddKnowledgeDropdown;
